import React, { Component } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import ExportableTable from 'containers/tables/ExportableTable';
import 'react-table-6/react-table.css';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import helpers from 'helpers';
import { generateFooterData, generateHeaders } from 'helpers/tables';
import { insertBeforeItem } from 'helpers/utilities';

const DEFAULT_ACCESSORS = [
    'age', 'gender', 'impressions', 'clicks', 'cpc', 'engagements',
    'ctr', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversionRate'
];

class DemographicsPivotTable extends Component {
    constructor(props) {
        super(props);

        let data = [];
        let columns;
        let uniqueRows;
        const { pivotBy } = this.props;

        if (props.hasDemographicsData) {
            const table = DemographicsPivotTable.createTableData(this.props.demographicsData, props);
            data = table.data;
            columns = table.columns;
            uniqueRows = new Set(data.map(c => c[pivotBy[0]])).size;
        }

        this.state = {
            data,
            columns,
            uniqueRows,
            pivotBy,
            hasDemographicsData: props.hasDemographicsData,
            exportName: props.exportName,
            demographicsParams: (this.props.demographicsParams || {}),
        };
    }

    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps.demographicsData, this.props.demographicsData)
            || !isEqual(nextProps.pivotBy, this.props.pivotBy);
    }

    static capitalizeGenders(data) {
        for (let i = 0; i < data.length; i += 1) {
            Object.keys(data[i]).forEach((key) => {
                if (key === 'gender') { data[i][key] = helpers.convertToDisplayValue(data[i][key]); }
            });
        }

        return data;
    }

    static createTableData(data = [], props) {
        DemographicsPivotTable.capitalizeGenders(data);
        const { entityObjectiveLabelCode, showSocialCpconv } = props;
        const keys = [
            'impressions', 'clicks', 'cpc', 'engagements', 'ctr', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversionRate', 'cost_per_conversion'
        ];

        let { accessors = DEFAULT_ACCESSORS } = props;

        // Only inject the column if we don't see it in the list of accessors
        if (showSocialCpconv && accessors.indexOf('cost_per_conversion') === -1) {
            accessors = insertBeforeItem(accessors, 'conversionRate', 'cost_per_conversion');
        }

        const headers = generateHeaders({
            keys,
            footerData: generateFooterData({ data, filteredKeys: ['age', 'gender'] }),
            colOptions: {
                labelCode: entityObjectiveLabelCode
            },
            initial: {
                age: {
                    Header: 'AGE',
                    accessor: 'age',
                    type: 'string',
                    minWidth: 140,
                    Aggregated: () => (<div/>),
                    Cell: row => <CellDiv value={row.value} style={{ textAlign: 'right' }} />,
                    PivotValue: ({ value }) => <span>{value}</span>,
                    Footer: (
                        <CellDiv value=' ' strong />
                    )
                },
                gender: {
                    Header: 'GENDER',
                    accessor: 'gender',
                    type: 'string',
                    Aggregated: () => (<div/>),
                    Cell: row => <CellDiv value={row.value} style={{ textAlign: 'right' }} />,
                    PivotValue: ({ value }) => <span>{value}</span>,
                    Footer: (
                        <CellDiv value=' ' strong />
                    )
                },
            }
        });


        const columns = accessors.map(accessor => headers[accessor]);

        return { data, columns };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextdemographicsData = nextProps.demographicsData;
        const nextDemographicsParams = nextProps.demographicsParams;
        const nextExportName = nextProps.exportName;
        const nextHasDemographicsData = nextProps.hasDemographicsData;
        const nextPivotBy = nextProps.pivotBy;
        let nextState = null;
        if (nextDemographicsParams !== prevState.demographicsParams || nextPivotBy !== prevState.pivotBy) {
            let data = [];
            let columns;
            let uniqueRows;

            if (nextHasDemographicsData) {
                const table = DemographicsPivotTable.createTableData(nextdemographicsData, nextProps);
                data = table.data;
                columns = table.columns;
                uniqueRows = new Set(data.map(c => c[nextPivotBy[0]])).size;
            }

            nextState = {
                ...prevState,
                columns,
                data,
                pivotBy: nextPivotBy,
                exportName: nextExportName,
                hasDemographicsData: nextHasDemographicsData,
                demographicsParams: nextDemographicsParams,
                uniqueRows,
            };
        }
        return nextState;
    }

    render() {
        const { data, exportName, columns, uniqueRows, pivotBy } = this.state;

        return (
            <ExportableTable
                key={1}
                title="DEMOGRAPHIC OVERVIEW"
                exportName={exportName}
                data={data}
                columns={columns}
                getTheadThProps={() => ({ style: { outline: 0 } })}
                getTfootProps= {() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                pivotBy={pivotBy}
                defaultSorted={[{ id: 'age', desc: false }, { id: 'gender', desc: false }]}
                showPagination={false}
                minRows={uniqueRows}
                exportRows={data.length}
                className="-highlight"
            />
        );
    }
}

DemographicsPivotTable.propTypes = {
    exportName: PropTypes.string.isRequired,
    demographicsData: PropTypes.arrayOf(PropTypes.shape({
        age: PropTypes.string,
        gender: PropTypes.string
    })),
    pivotBy: PropTypes.arrayOf(PropTypes.string),
    accessors: PropTypes.arrayOf(PropTypes.string),
    hasDemographicsData: PropTypes.bool.isRequired,
    entityObjectiveLabelCode: PropTypes.string
};

export default DemographicsPivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/DemographicsPivotTable.js